import { campaignConfig } from '../content/config';
import { assetManifest } from '../content/assets';
import { inventoryItems } from '../content/items';
import { visualAssets } from '../content/visualAssets';
import { selectableLocationForMission } from '../content/locations';
import { MissionMap } from './MissionMap';
import type { DialogueService } from '../domain/dialogue/DialogueService';
import type { EffectExecutor } from '../domain/effects/EffectExecutor';
import type { InventoryService } from '../domain/inventory/InventoryService';
import type { ConditionEvaluator } from '../domain/rules/ConditionEvaluator';
import type {
  AdventureSceneDefinition,
  DialogueDefinition,
  DialogueNode,
  ActionDefinition,
  HotspotDefinition,
} from '../domain/types';
import { createInitialState, type GameStateStore } from '../domain/state/GameStateStore';
import type { SaveRepository } from '../domain/save/SaveRepository';
import type { EventBus } from '../game/events/EventBus';
import type { AudioManager } from '../audio/AudioManager';

interface ActionMenuState {
  hotspot: HotspotDefinition;
  actions: ActionDefinition[];
  x: number;
  y: number;
}

interface DialogueState {
  dialogue: DialogueDefinition;
  node: DialogueNode;
}

type View = 'start' | 'map' | 'adventure';

export class AppShell {
  #started = false;
  #view: View = 'start';
  #actionMenu: ActionMenuState | null = null;
  #dialogue: DialogueState | null = null;
  #inventoryOpen = false;
  #inventoryMenuItemId: string | null = null;
  #inspectedItemId: string | null = null;
  #choosingItemTarget = false;
  #settingsOpen = false;
  #historyOpen = false;
  readonly #missionMap: MissionMap;
  #toast: { text: string; kind: string } | null = null;
  #tooltip: { label: string; x: number; y: number } | null = null;
  #hotspots: HotspotDefinition[] = [];
  #currentScene: AdventureSceneDefinition | null = null;
  #debugPointer = { x: 0, y: 0, normalizedX: 0, normalizedY: 0 };
  #selectedDebugHotspotId: string | null = null;
  #toastTimer = 0;
  #loadingProgress = 0;
  #animationBoundsVisible = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly events: EventBus,
    private readonly store: GameStateStore,
    private readonly saves: SaveRepository,
    private readonly inventory: InventoryService,
    private readonly evaluator: ConditionEvaluator,
    private readonly dialogueService: DialogueService,
    private readonly effects: EffectExecutor,
    private readonly audio: AudioManager,
    private readonly debugEnabled: boolean,
  ) {
    this.#missionMap = new MissionMap(
      this.root,
      () => this.#render(),
      (missionId) => {
        const location = selectableLocationForMission(
          missionId,
          this.store.snapshot.completedPuzzles,
        );
        if (location) this.events.emit('ui:openLocation', { location });
      },
      () => this.store.snapshot.completedPuzzles,
    );
    this.#bindEvents();
    this.#render();
    void this.#missionMap.load();
  }

  #bindEvents(): void {
    this.events.on('scene:map', () => {
      if (!this.#started) return;
      this.#view = 'map';
      this.#currentScene = null;
      this.#closeTransientUi();
      this.#render();
    });
    this.events.on('loading:progress', ({ progress }) => {
      this.#loadingProgress = progress;
      if (!this.#started) this.#render();
    });
    this.events.on('scene:adventure', (scene) => {
      if (!this.#started) return;
      this.#view = 'adventure';
      this.#currentScene = scene;
      this.#closeTransientUi();
      this.#render();
    });
    this.events.on('scene:hotspots', (hotspots) => {
      this.#hotspots = hotspots;
      this.#render();
    });
    this.events.on('hotspot:hover', (tooltip) => {
      this.#tooltip = tooltip;
      this.#updateTooltip();
    });
    this.events.on('hotspot:leave', () => {
      this.#tooltip = null;
      this.#updateTooltip();
    });
    this.events.on('hotspot:actions', ({ hotspot, actions, x, y }) => {
      if (this.#choosingItemTarget && this.store.snapshot.selectedItemId) {
        void this.#useSelectedItemOn(hotspot);
        return;
      }
      this.#actionMenu = { hotspot, actions, x, y };
      this.#tooltip = null;
      this.#render();
      window.setTimeout(
        () => this.root.querySelector<HTMLButtonElement>('.action-menu button')?.focus(),
        0,
      );
    });
    this.events.on('dialogue:start', ({ dialogue, node }) => {
      this.#dialogue = { dialogue, node };
      void this.effects.executeAll(node.effects);
      this.#render();
    });
    this.events.on('dialogue:end', () => {
      this.#dialogue = null;
      this.#render();
    });
    this.events.on('message:show', ({ text, kind = 'info' }) => this.#showToast(text, kind));
    this.events.on('debug:pointer', (pointer) => {
      this.#debugPointer = pointer;
      const output = this.root.querySelector<HTMLElement>('[data-debug-pointer]');
      if (output) output.textContent = this.#pointerText();
    });
    this.store.subscribe((state) => {
      this.saves.save(state);
      this.events.emit('state:changed', state);
      this.audio.applySettings(state.settings);
      this.#render();
    });

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.#actionMenu = null;
        this.#dialogue = null;
        this.#inventoryOpen = false;
        this.#inventoryMenuItemId = null;
        this.#inspectedItemId = null;
        this.#choosingItemTarget = false;
        this.#settingsOpen = false;
        this.#historyOpen = false;
        this.inventory.select(null);
        this.#render();
        this.#missionMap.closePanel(true);
      }
    });
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.#actionMenu) return;
        const target = event.target;
        if (target instanceof Element && target.closest('.action-menu')) return;

        // Det første klik udenfor lukker kun objektmenuen og må ikke ramme scenen bagved.
        event.preventDefault();
        event.stopPropagation();
        this.#actionMenu = null;
        this.#tooltip = null;
        this.#render();
      },
      { capture: true },
    );
  }

  #render(): void {
    this.root.innerHTML = `
      <div class="ui-layer ${this.#view} ${this.store.snapshot.settings.reducedMotion ? 'reduced-motion' : ''}">
        ${this.#view === 'start' ? this.#startScreen() : this.#gameHud()}
        ${this.#view === 'map' ? this.#missionMap.markup() : ''}
        ${this.#itemTargetMarkup()}
        <div class="tooltip-host">${this.#tooltipMarkup()}</div>
        ${this.#actionMenuMarkup()}
        ${this.#dialogueMarkup()}
        ${this.#inventoryMarkup()}
        ${this.#settingsMarkup()}
        ${this.#historyMarkup()}
        ${this.#keyboardHotspotsMarkup()}
        ${this.#toastMarkup()}
        ${this.debugEnabled ? this.#debugMarkup() : ''}
        <div class="narrow-screen-note" role="note">Denne demo opleves bedst på en PC eller Mac.</div>
        <div class="sr-only" aria-live="polite" aria-atomic="true">${this.#escape(this.#toast?.text ?? this.#tooltip?.label ?? '')}</div>
      </div>`;
    this.root
      .querySelector<HTMLElement>('.start-screen')
      ?.style.setProperty('--intro-image', `url("${visualAssets.intro}")`);
    this.#bindDomActions();
    this.events.emit('ui:inputBlocked', {
      blocked:
        !this.#started ||
        Boolean(
          this.#actionMenu ||
          this.#dialogue ||
          this.#inventoryOpen ||
          this.#settingsOpen ||
          this.#historyOpen ||
          this.#missionMap.inputBlocked,
        ),
    });
  }

  #startScreen(): string {
    const hasSave = this.saves.exists();
    const musicOn = this.audio.playing;
    return `<section class="start-screen" aria-labelledby="game-title" aria-describedby="game-description">
      <h1 id="game-title" class="sr-only">${campaignConfig.title}</h1>
      <p id="game-description" class="sr-only">Vejles dokumenterede historie bliver til et levende point-and-click-eventyr.</p>
      <button class="intro-audio-toggle ${musicOn ? 'is-on' : ''}" data-action="toggle-intro-audio" data-testid="intro-audio-toggle" aria-pressed="${musicOn}" aria-label="${musicOn ? 'Slå musikken fra' : 'Slå musikken til'}" title="${musicOn ? 'Slå musikken fra' : 'Slå musikken til'}">
        <span class="intro-audio-icon" aria-hidden="true">${musicOn ? '🔊' : '🔇'}</span>
        <span class="intro-audio-label">${musicOn ? 'Lyd til' : 'Lyd fra'}</span>
      </button>
      <div class="intro-atmosphere" aria-hidden="true">
        <span class="intro-cloud intro-cloud-moon"></span>
        <span class="intro-cloud intro-cloud-moon-far"></span>
      </div>
      <div class="intro-menu">
        <p class="intro-menu-label">${campaignConfig.edition}</p>
        <div class="intro-actions" aria-label="Hovedmenu">
          <button class="${hasSave ? 'primary-button' : ''}" data-action="start" data-testid="start-game" ${hasSave ? '' : 'disabled aria-disabled="true"'}>Fortsæt spillet</button>
          <button class="${hasSave ? '' : 'primary-button'}" data-action="new-game" data-testid="new-game">Nyt spil</button>
        </div>
        <label class="intro-loading"><span>Indlæser</span><progress max="1" value="${this.#loadingProgress}"></progress></label>
      </div>
    </section>`;
  }

  #gameHud(): string {
    return `<header class="game-hud ${this.#view === 'map' ? 'map-hud' : ''}">
      <div class="hud-title">
        <span class="eyebrow">${campaignConfig.edition}</span>
        <strong>${this.#view === 'map' ? 'Vejlekortet' : (this.#currentScene?.title ?? campaignConfig.title)}</strong>
      </div>
      <nav aria-label="Spilmenu">
        ${this.#view === 'map' ? '<button data-action="return-start" data-testid="return-start">← Start</button>' : ''}
        ${this.#view === 'adventure' ? '<button data-action="return-map" data-testid="return-map">← Kortet</button>' : ''}
        <button data-action="open-inventory" data-testid="open-inventory">Rygsæk <span class="count">${this.store.snapshot.inventory.length}</span></button>
        ${this.#currentScene ? '<button data-action="open-history">Historie & kilder</button>' : ''}
        <button data-action="open-settings" aria-label="Åbn indstillinger">⚙ Indstillinger</button>
        ${this.#view === 'map' ? `<a class="hud-link" href="${campaignConfig.websiteUrl}" target="_blank" rel="noreferrer">Oplev stederne i virkeligheden ↗</a>` : ''}
      </nav>
    </header>`;
  }

  #tooltipMarkup(): string {
    if (!this.#tooltip || this.#actionMenu) return '';
    return `<div class="hotspot-tooltip" style="--x:${this.#tooltip.x / 19.2}%;--y:${this.#tooltip.y / 10.8}%" role="tooltip">${this.#escape(this.#tooltip.label)}</div>`;
  }

  #itemTargetMarkup(): string {
    const selectedId = this.store.snapshot.selectedItemId;
    const selected = selectedId ? inventoryItems[selectedId] : undefined;
    if (!this.#choosingItemTarget || !selected || this.#view !== 'adventure') return '';
    return `<aside class="item-target-prompt" aria-live="polite" data-testid="item-target-prompt">
      <span>Brug <strong>${this.#escape(selected.name)}</strong> på …</span>
      <button data-action="cancel-item-target">Annuller</button>
    </aside>`;
  }

  #updateTooltip(): void {
    const host = this.root.querySelector<HTMLElement>('.tooltip-host');
    if (host) host.innerHTML = this.#tooltipMarkup();
    const live = this.root.querySelector<HTMLElement>('.sr-only[aria-live]');
    if (live) live.textContent = this.#tooltip?.label ?? '';
  }

  #actionMenuMarkup(): string {
    if (!this.#actionMenu) return '';
    const { hotspot, actions, x, y } = this.#actionMenu;
    return `<div class="action-menu paper-panel" style="--x:${x / 19.2}%;--y:${y / 10.8}%" role="menu" aria-label="Handlinger for ${this.#escape(hotspot.label)}">
      <strong>${this.#escape(hotspot.label)}</strong>
      ${actions.map((action) => `<button role="menuitem" data-action-id="${action.id}" data-testid="action-${action.id}">${this.#escape(action.label)}</button>`).join('')}
    </div>`;
  }

  #dialogueMarkup(): string {
    if (!this.#dialogue) return '';
    const { dialogue, node } = this.#dialogue;
    const choices = this.dialogueService.availableChoices(node);
    const controls = choices.length
      ? choices
          .map(
            (choice, index) =>
              `<button data-choice-id="${choice.id}" data-testid="choice-${choice.id}"><span>${index + 1}</span>${this.#escape(choice.text)}</button>`,
          )
          .join('')
      : `<button class="primary-button" data-action="dialogue-next" data-testid="dialogue-next">${node.nextNodeId ? 'Fortsæt' : 'Luk'}</button>`;
    return `<section class="dialogue-panel" aria-live="polite" aria-label="Dialog">
      <div class="dialogue-portrait" aria-hidden="true">${node.speaker === 'Soldaten' ? 'S' : 'V'}</div>
      <div class="dialogue-content">
        <div class="dialogue-meta"><strong>${this.#escape(node.speaker)}</strong><span>${this.#escape(dialogue.fictionLabel ?? '')}</span></div>
        <p>${this.#escape(node.text)}</p>
        <div class="dialogue-choices">${controls}</div>
      </div>
    </section>`;
  }

  #inventoryMarkup(): string {
    if (!this.#inventoryOpen) return '';
    const selected = this.store.snapshot.selectedItemId;
    const menuItem = this.#inventoryMenuItemId
      ? inventoryItems[this.#inventoryMenuItemId]
      : undefined;
    const inspectedItem = this.#inspectedItemId ? inventoryItems[this.#inspectedItemId] : undefined;
    const items = this.store.snapshot.inventory
      .map((id) => inventoryItems[id])
      .filter((item) => item !== undefined);
    return `<aside class="inventory-panel inventory-drawer paper-panel" role="dialog" aria-labelledby="inventory-title">
      <div class="panel-heading"><div><p class="eyebrow">Inventory</p><h2 id="inventory-title">Rygsækken</h2></div><button data-action="close-panels" aria-label="Luk rygsækken">✕</button></div>
      ${
        items.length === 0
          ? '<p class="empty-state">Rygsækken er tom. Der er sikkert noget at finde.</p>'
          : `
        <div class="inventory-grid">${items
          .map(
            (
              item,
            ) => `<button class="inventory-item ${selected === item.id ? 'selected' : ''}" data-item-id="${item.id}" data-testid="inventory-${item.id}">
          ${item.id === 'packedLunch' ? '<span class="inventory-item-emoji" aria-hidden="true">🥪</span>' : `<img src="${Object.values(assetManifest).find((asset) => asset.id === item.iconAssetId)?.path ?? ''}" alt="" />`}
          <strong>${this.#escape(item.name)}</strong>
        </button>`,
          )
          .join('')}</div>
        ${menuItem ? `<div class="action-menu inventory-action-menu paper-panel" role="menu" aria-label="Handlinger for ${this.#escape(menuItem.name)}"><strong>${this.#escape(menuItem.name)}</strong><button role="menuitem" data-action="choose-item-target" data-testid="choose-item-target" ${this.#view === 'adventure' ? '' : 'disabled'}>Vælg mål</button><button role="menuitem" data-action="inspect-inventory-item" data-testid="inspect-inventory-item">Se på</button></div>` : ''}
        ${inspectedItem ? `<article class="item-detail" data-testid="inventory-item-description"><p class="eyebrow">${this.#escape(inspectedItem.name)}</p><p>${this.#escape(inspectedItem.longDescription ?? inspectedItem.shortDescription)}</p>${inspectedItem.historicalNote ? `<p>${this.#escape(inspectedItem.historicalNote)}</p>` : ''}</article>` : ''}`
      }
    </aside>`;
  }

  #settingsMarkup(): string {
    if (!this.#settingsOpen) return '';
    const settings = this.store.snapshot.settings;
    const fullscreenAvailable = Boolean(document.fullscreenEnabled);
    return `<section class="modal-backdrop"><div class="settings-panel paper-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <div class="panel-heading"><div><p class="eyebrow">Spilmenu</p><h2 id="settings-title">Indstillinger</h2></div><button data-action="close-panels" aria-label="Luk indstillinger">✕</button></div>
      <label>Musik <output>${Math.round(settings.musicVolume * 100)} %</output><input data-setting="musicVolume" type="range" min="0" max="1" step="0.05" value="${settings.musicVolume}" /></label>
      <label>Lydeffekter <output>${Math.round(settings.sfxVolume * 100)} %</output><input data-setting="sfxVolume" type="range" min="0" max="1" step="0.05" value="${settings.sfxVolume}" /></label>
      <label class="toggle"><input data-setting="muted" type="checkbox" ${settings.muted ? 'checked' : ''} /> Slå al lyd fra</label>
      <label class="toggle"><input data-setting="reducedMotion" type="checkbox" ${settings.reducedMotion ? 'checked' : ''} /> Reducer bevægelse</label>
      <div class="settings-actions">
        <button data-action="fullscreen" ${fullscreenAvailable ? '' : 'disabled'}>Fuld skærm</button>
        <button class="danger-button" data-action="reset" data-testid="reset-game">Nulstil spil</button>
      </div>
      <p class="fine-print">“Vejles Koder Nynne” afspilles i loop. Ambience og lydeffekter er fortsat enkle demo-placeholders.</p>
    </div></section>`;
  }

  #historyMarkup(): string {
    if (!this.#historyOpen || !this.#currentScene) return '';
    const scene = this.#currentScene;
    const sources = scene.sourceReferences?.length
      ? `<ul>${scene.sourceReferences.map((source) => `<li>${this.#escape(source.title)}</li>`).join('')}</ul>`
      : '<p>Ingen kilder er indsat i demoen endnu. Der opfindes ikke midlertidige kilder.</p>';
    return `<section class="modal-backdrop"><div class="history-panel paper-panel" role="dialog" aria-modal="true" aria-labelledby="history-title">
      <div class="panel-heading"><div><p class="eyebrow">Fakta og fiktion</p><h2 id="history-title">${this.#escape(scene.title)}</h2></div><button data-action="close-panels" aria-label="Luk historisk info">✕</button></div>
      <section><h3>Historisk fakta</h3>${(scene.documentedHistory ?? []).map((text) => `<p>${this.#escape(text)}</p>`).join('')}</section>
      <section class="fiction-block"><h3>Spillets fiktive ramme</h3><p>${this.#escape(scene.fictionLabel ?? '')}</p>${(scene.gameplayFiction ?? []).map((text) => `<p>${this.#escape(text)}</p>`).join('')}</section>
      <section><h3>Kilder</h3>${sources}</section>
    </div></section>`;
  }

  #keyboardHotspotsMarkup(): string {
    if (this.#view !== 'adventure' || this.#dialogue || this.#settingsOpen || this.#inventoryOpen)
      return '';
    return `<div class="keyboard-hotspots" aria-label="Objekter i scenen">${this.#hotspots
      .map((hotspot) => {
        const anchor =
          hotspot.shape.type === 'rect'
            ? {
                x: hotspot.shape.x + hotspot.shape.width / 2,
                y: hotspot.shape.y + hotspot.shape.height / 2,
              }
            : (hotspot.shape.points[0] ?? { x: 0, y: 0 });
        return `<button class="keyboard-hotspot" style="--x:${anchor.x / 19.2}%;--y:${anchor.y / 10.8}%" data-hotspot-id="${hotspot.id}" aria-label="${this.#escape(hotspot.label)}"><span aria-hidden="true">${this.#escape(hotspot.label)}</span></button>`;
      })
      .join('')}</div>`;
  }

  #toastMarkup(): string {
    return this.#toast
      ? `<div class="toast ${this.#toast.kind}" role="status">${this.#escape(this.#toast.text)}</div>`
      : '';
  }

  #debugMarkup(): string {
    const selected =
      this.#hotspots.find((hotspot) => hotspot.id === this.#selectedDebugHotspotId) ??
      this.#hotspots[0];
    const rectControls =
      selected?.shape.type === 'rect'
        ? `<div class="debug-grid">${(['x', 'y', 'width', 'height'] as const).map((key) => `<label>${key}<input type="number" data-debug-shape="${key}" value="${selected.shape.type === 'rect' ? selected.shape[key] : 0}" /></label>`).join('')}</div>`
        : '<p>Polygoner kopieres som JSON; direkte flytning er kun implementeret for rectangles i demoen.</p>';
    return `<aside class="debug-panel" aria-label="Hotspot-forfattertilstand">
      <div class="panel-heading"><div><p class="eyebrow">Forfattertilstand</p><h2>Hotspots</h2></div></div>
      <output data-debug-pointer>${this.#pointerText()}</output>
      <label>Vælg hotspot<select data-debug-select>${this.#hotspots.map((hotspot) => `<option value="${hotspot.id}" ${hotspot.id === selected?.id ? 'selected' : ''}>${hotspot.id} · ${this.#escape(hotspot.label)}</option>`).join('')}</select></label>
      ${selected ? rectControls : '<p>Åbn en scene for at redigere hotspots.</p>'}
      <label class="toggle"><input data-debug-animation-bounds type="checkbox" ${this.#animationBoundsVisible ? 'checked' : ''} /> Vis animation bounds</label>
      <button data-action="copy-hotspot" ${selected ? '' : 'disabled'}>Kopiér hotspot-JSON</button>
      <details><summary>Flags og inventory</summary><pre>${this.#escape(JSON.stringify({ flags: this.store.snapshot.flags, inventory: this.store.snapshot.inventory }, null, 2))}</pre></details>
      <div class="debug-flag"><input data-debug-flag-key placeholder="flag-id" aria-label="Flag-id" /><button data-action="set-flag">Sæt true</button><button data-action="unset-flag">Sæt false</button></div>
      <button class="danger-button" data-action="reset">Nulstil save</button>
    </aside>`;
  }

  #bindDomActions(): void {
    this.#missionMap.bind();
    this.root
      .querySelector('[data-action="start"]')
      ?.addEventListener('click', () => void this.#start(true));
    this.root
      .querySelector('[data-action="new-game"]')
      ?.addEventListener('click', () => void this.#newGame());
    this.root
      .querySelector('[data-action="toggle-intro-audio"]')
      ?.addEventListener('click', () => void this.#toggleIntroAudio());
    this.root.querySelectorAll('[data-action="open-settings"]').forEach((button) =>
      button.addEventListener('click', () => {
        this.#settingsOpen = true;
        this.#render();
      }),
    );
    this.root.querySelectorAll('[data-action="open-inventory"]').forEach((button) =>
      button.addEventListener('click', () => {
        this.#choosingItemTarget = false;
        this.#inventoryMenuItemId = null;
        this.#inspectedItemId = null;
        this.#inventoryOpen = true;
        if (this.store.snapshot.selectedItemId) this.inventory.select(null);
        else this.#render();
      }),
    );
    this.root.querySelector('[data-action="open-history"]')?.addEventListener('click', () => {
      this.#historyOpen = true;
      this.#render();
    });
    this.root.querySelectorAll('[data-action="close-panels"]').forEach((button) =>
      button.addEventListener('click', () => {
        const closingInventory = this.#inventoryOpen;
        this.#settingsOpen = false;
        this.#inventoryOpen = false;
        this.#inventoryMenuItemId = null;
        this.#inspectedItemId = null;
        this.#historyOpen = false;
        if (closingInventory && this.store.snapshot.selectedItemId) this.inventory.select(null);
        this.#render();
      }),
    );
    this.root.querySelector('[data-action="choose-item-target"]')?.addEventListener('click', () => {
      if (!this.store.snapshot.selectedItemId) return;
      this.#inventoryOpen = false;
      this.#inventoryMenuItemId = null;
      this.#inspectedItemId = null;
      this.#choosingItemTarget = true;
      this.#actionMenu = null;
      this.#render();
    });
    this.root.querySelector('[data-action="cancel-item-target"]')?.addEventListener('click', () => {
      this.#cancelItemTarget();
    });
    this.root
      .querySelector('[data-action="inspect-inventory-item"]')
      ?.addEventListener('click', () => {
        this.#inspectedItemId = this.#inventoryMenuItemId;
        this.#inventoryMenuItemId = null;
        this.#render();
      });
    this.root
      .querySelector('[data-action="return-map"]')
      ?.addEventListener('click', () => this.events.emit('ui:returnToMap'));
    this.root
      .querySelector('[data-action="return-start"]')
      ?.addEventListener('click', () => this.#returnToStart());

    this.root.querySelectorAll<HTMLElement>('[data-action-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const action = this.#actionMenu?.hotspot.actions.find(
          (entry) => entry.id === button.dataset.actionId,
        );
        this.#actionMenu = null;
        if (action) void this.effects.executeAction(action);
        this.#render();
      });
    });
    this.root.querySelectorAll<HTMLElement>('[data-hotspot-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const hotspot = this.#hotspots.find((entry) => entry.id === button.dataset.hotspotId);
        if (!hotspot) return;
        if (this.#choosingItemTarget && this.store.snapshot.selectedItemId) {
          void this.#useSelectedItemOn(hotspot);
          return;
        }
        const anchor =
          hotspot.shape.type === 'rect'
            ? {
                x: hotspot.shape.x + hotspot.shape.width / 2,
                y: hotspot.shape.y + hotspot.shape.height / 2,
              }
            : (hotspot.shape.points[0] ?? { x: 960, y: 540 });
        const actions = hotspot.actions.filter((action) => {
          const itemRelevant =
            this.store.snapshot.selectedItemId === null ||
            action.itemId === this.store.snapshot.selectedItemId ||
            action.verb !== 'use';
          const available =
            this.evaluator.evaluateAll(action.conditions, this.store.snapshot) ||
            Boolean(action.unavailableMessage);
          return itemRelevant && available;
        });
        this.#actionMenu = { hotspot, actions, ...anchor };
        this.#render();
      });
    });

    this.root
      .querySelector('[data-action="dialogue-next"]')
      ?.addEventListener('click', () => void this.#advanceDialogue());
    this.root
      .querySelectorAll<HTMLElement>('[data-choice-id]')
      .forEach((button) =>
        button.addEventListener(
          'click',
          () => void this.#chooseDialogue(button.dataset.choiceId ?? ''),
        ),
      );
    this.root.querySelectorAll<HTMLElement>('[data-item-id]').forEach((button) =>
      button.addEventListener('click', () => {
        const id = button.dataset.itemId ?? null;
        this.#inventoryMenuItemId = id;
        this.#inspectedItemId = null;
        this.inventory.select(id);
      }),
    );

    this.root.querySelectorAll<HTMLInputElement>('[data-setting]').forEach((input) =>
      input.addEventListener('input', () => {
        const key = input.dataset.setting;
        if (key === 'musicVolume' || key === 'sfxVolume')
          this.store.updateSettings({ [key]: Number(input.value) });
        if (key === 'muted' || key === 'reducedMotion')
          this.store.updateSettings({ [key]: input.checked });
      }),
    );
    this.root
      .querySelector<HTMLInputElement>('[data-debug-animation-bounds]')
      ?.addEventListener('change', (event) => {
        this.#animationBoundsVisible = (event.target as HTMLInputElement).checked;
        this.events.emit('ui:debugAnimationBounds', { visible: this.#animationBoundsVisible });
      });
    this.root
      .querySelector('[data-action="fullscreen"]')
      ?.addEventListener(
        'click',
        () => void document.getElementById('game-frame')?.requestFullscreen(),
      );
    this.root
      .querySelectorAll('[data-action="reset"]')
      .forEach((button) => button.addEventListener('click', () => void this.#reset()));

    this.root
      .querySelector<HTMLSelectElement>('[data-debug-select]')
      ?.addEventListener('change', (event) => {
        this.#selectedDebugHotspotId = (event.target as HTMLSelectElement).value;
        this.#render();
      });
    this.root
      .querySelectorAll<HTMLInputElement>('[data-debug-shape]')
      .forEach((input) => input.addEventListener('change', () => this.#changeDebugRect()));
    this.root
      .querySelector('[data-action="copy-hotspot"]')
      ?.addEventListener('click', () => void this.#copyHotspot());
    this.root
      .querySelector('[data-action="set-flag"]')
      ?.addEventListener('click', () => this.#setDebugFlag(true));
    this.root
      .querySelector('[data-action="unset-flag"]')
      ?.addEventListener('click', () => this.#setDebugFlag(false));
  }

  async #start(continueGame: boolean): Promise<void> {
    await this.audio.activate();
    this.#started = true;
    this.#view = this.store.snapshot.currentSceneId ? 'adventure' : 'map';
    this.events.emit('ui:start', { continueGame });
    this.#render();
  }

  async #toggleIntroAudio(): Promise<void> {
    if (this.audio.playing) {
      this.audio.deactivate();
      this.#render();
      return;
    }
    if (this.store.snapshot.settings.muted) {
      this.store.update((draft) => {
        draft.settings.muted = false;
      });
    }
    if (!(await this.audio.activate())) {
      this.#showToast('Browseren kunne ikke starte musikken.', 'warning');
      return;
    }
    this.#render();
  }

  async #newGame(): Promise<void> {
    this.saves.clear();
    this.store.replace(createInitialState());
    await this.#start(false);
  }

  #returnToStart(): void {
    this.#missionMap.closePanel();
    this.#started = false;
    this.#view = 'start';
    this.#currentScene = null;
    this.#closeTransientUi();
    this.#render();
  }

  async #reset(): Promise<void> {
    if (!window.confirm('Nulstil spillet? Inventory, besøg og alle valg slettes.')) return;
    this.saves.clear();
    this.store.replace(createInitialState());
    this.#settingsOpen = false;
    this.#inventoryOpen = false;
    this.#view = 'map';
    this.events.emit('ui:returnToMap');
    this.#showToast('Spillet er nulstillet.', 'warning');
  }

  async #advanceDialogue(): Promise<void> {
    if (!this.#dialogue) return;
    const { dialogue, node } = this.#dialogue;
    const next = this.dialogueService.next(dialogue, node);
    if (!next) {
      this.#dialogue = null;
      this.events.emit('dialogue:end');
      return;
    }
    await this.effects.executeAll(next.effects);
    this.#dialogue = { dialogue, node: next };
    this.#render();
  }

  async #chooseDialogue(choiceId: string): Promise<void> {
    if (!this.#dialogue) return;
    const { dialogue, node } = this.#dialogue;
    const choice = this.dialogueService
      .availableChoices(node)
      .find((entry) => entry.id === choiceId);
    if (!choice) return;
    await this.effects.executeAll(choice.effects);
    const next = this.dialogueService.next(dialogue, node, choice);
    if (!next) {
      this.#dialogue = null;
      this.events.emit('dialogue:end');
      return;
    }
    await this.effects.executeAll(next.effects);
    this.#dialogue = { dialogue, node: next };
    this.#render();
  }

  #showToast(text: string, kind: string): void {
    window.clearTimeout(this.#toastTimer);
    this.#toast = { text, kind };
    this.#render();
    this.#toastTimer = window.setTimeout(() => {
      this.#toast = null;
      this.#render();
    }, 3600);
  }

  #closeTransientUi(): void {
    this.#missionMap.closePanel();
    this.#actionMenu = null;
    this.#dialogue = null;
    this.#tooltip = null;
    this.#inventoryMenuItemId = null;
    this.#inspectedItemId = null;
    this.#choosingItemTarget = false;
    if (this.store.snapshot.selectedItemId) this.inventory.select(null);
  }

  async #useSelectedItemOn(hotspot: HotspotDefinition): Promise<void> {
    const selectedItemId = this.store.snapshot.selectedItemId;
    if (!selectedItemId) return;
    const action = hotspot.actions.find(
      (candidate) =>
        candidate.itemId === selectedItemId &&
        this.evaluator.evaluateAll(candidate.conditions, this.store.snapshot),
    );
    this.#actionMenu = null;
    this.#tooltip = null;
    if (!action) {
      this.#showToast('Det giver ikke mening at bruge den her.', 'warning');
      return;
    }

    this.#choosingItemTarget = false;
    await this.effects.executeAction(action);
    if (this.store.snapshot.selectedItemId) this.inventory.select(null);
  }

  #cancelItemTarget(): void {
    this.#choosingItemTarget = false;
    if (this.store.snapshot.selectedItemId) this.inventory.select(null);
    this.#render();
  }

  #changeDebugRect(): void {
    const selected =
      this.#hotspots.find((hotspot) => hotspot.id === this.#selectedDebugHotspotId) ??
      this.#hotspots[0];
    if (!selected || selected.shape.type !== 'rect') return;
    const updated = structuredClone(selected);
    if (updated.shape.type !== 'rect') return;
    for (const input of this.root.querySelectorAll<HTMLInputElement>('[data-debug-shape]')) {
      const key = input.dataset.debugShape as 'x' | 'y' | 'width' | 'height';
      updated.shape[key] = Number(input.value);
    }
    this.events.emit('ui:debugHotspotChanged', { hotspot: updated });
  }

  async #copyHotspot(): Promise<void> {
    const selected =
      this.#hotspots.find((hotspot) => hotspot.id === this.#selectedDebugHotspotId) ??
      this.#hotspots[0];
    if (!selected) return;
    await navigator.clipboard.writeText(JSON.stringify(selected, null, 2));
    this.#showToast('Hotspot-JSON er kopieret.', 'info');
  }

  #setDebugFlag(value: boolean): void {
    const input = this.root.querySelector<HTMLInputElement>('[data-debug-flag-key]');
    const key = input?.value.trim();
    if (key) this.store.setFlag(key, value);
  }

  #pointerText(): string {
    const pointer = this.#debugPointer;
    return `x ${pointer.x} · y ${pointer.y} · nx ${pointer.normalizedX} · ny ${pointer.normalizedY}`;
  }

  #escape(value: string): string {
    return value.replace(
      /[&<>'"]/g,
      (character) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character] ??
        character,
    );
  }
}
